import { X, ArrowUp, Plus } from "lucide-react";
import { imgUrl } from "@/lib/api";
import { ImageUploader } from "./ImageUploader";
import { RichEditor } from "./RichEditor";
import { CategoryCoversField } from "./CategoryCoversField";
import { CategoryPicker } from "./CategoryPicker";

const Label = ({ children }) => <span className="block text-xs text-slate-500 mb-1">{children}</span>;
const move = (arr, k) => { const a = [...arr]; [a[k - 1], a[k]] = [a[k], a[k - 1]]; return a; };

const ImageField = ({ fl, d, set, id }) => (
  <div className="space-y-2">
    {d[fl.key] && <div className="relative w-40"><img src={imgUrl(d[fl.key])} alt="" className="w-40 h-28 object-cover rounded" />
      <button type="button" onClick={() => set({ [fl.key]: "" })} className="absolute top-1 right-1 bg-white/90 rounded p-1 text-terracotta" aria-label="Rimuovi"><X size={13} /></button></div>}
    <div className="flex flex-wrap gap-2 items-center">
      <ImageUploader multiple={false} label={d[fl.key] ? "Sostituisci" : "Carica immagine"} onUploaded={(u) => set({ [fl.key]: u[0] })} testid={`${id}-upload`} />
      <input className="admin-input !w-64" placeholder="oppure indirizzo immagine" value={d[fl.key] || ""} onChange={(e) => set({ [fl.key]: e.target.value })} data-testid={`${id}-url`} />
    </div>
    <input className="admin-input" placeholder="Testo alternativo (descrizione per Google e accessibilità)" value={d[`${fl.key}_alt`] || ""} onChange={(e) => set({ [`${fl.key}_alt`]: e.target.value })} data-testid={`${id}-alt`} />
  </div>
);

const ImagesField = ({ value = [], onChange, id }) => (
  <div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
      {value.map((p, k) => (
        <div key={p.src + k} className="space-y-1">
          <div className="relative aspect-square bg-slate-100 rounded overflow-hidden"><img src={imgUrl(p.src)} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-1 right-1 flex gap-1">
              {k > 0 && <button type="button" onClick={() => onChange(move(value, k))} className="bg-white/90 rounded p-1" aria-label="Sposta prima"><ArrowUp size={12} className="-rotate-90" /></button>}
              <button type="button" onClick={() => onChange(value.filter((_, i) => i !== k))} className="bg-white/90 rounded p-1 text-terracotta" aria-label="Rimuovi" data-testid={`${id}-remove-${k}`}><X size={12} /></button>
            </div></div>
          <input className="admin-input !text-xs !py-1" placeholder="Testo alternativo" value={p.alt} onChange={(e) => onChange(value.map((x, i) => (i === k ? { ...x, alt: e.target.value } : x)))} />
        </div>))}
    </div>
    <ImageUploader onUploaded={(u) => onChange([...value, ...u.map((src) => ({ src, alt: "" }))])} testid={`${id}-upload`} />
  </div>
);

const ItemsField = ({ value = [], onChange, id }) => (
  <div className="space-y-2">
    {value.map((it, k) => (
      <div key={k} className="flex gap-2 items-start">
        <div className="flex-1 space-y-1">
          <input className="admin-input" placeholder="Titolo" value={it.title} onChange={(e) => onChange(value.map((x, i) => (i === k ? { ...x, title: e.target.value } : x)))} data-testid={`${id}-title-${k}`} />
          <textarea className="admin-input min-h-[44px]" placeholder="Testo (facoltativo)" value={it.text} onChange={(e) => onChange(value.map((x, i) => (i === k ? { ...x, text: e.target.value } : x)))} />
        </div>
        {k > 0 && <button type="button" onClick={() => onChange(move(value, k))} className="p-2 text-slate-500" aria-label="Sposta su"><ArrowUp size={14} /></button>}
        <button type="button" onClick={() => onChange(value.filter((_, i) => i !== k))} className="p-2 text-terracotta" aria-label="Rimuovi"><X size={14} /></button>
      </div>))}
    <button type="button" onClick={() => onChange([...value, { title: "", text: "" }])} className="text-sm text-sagedark inline-flex items-center gap-1" data-testid={`${id}-add`}><Plus size={14} /> Aggiungi voce</button>
  </div>
);

export const BlockForm = ({ section, fields, onChange, links }) => {
  const d = section.data;
  const set = (p) => onChange({ ...section, data: { ...d, ...p } });
  return (
    <div className="space-y-5" data-testid="block-form">
      {fields.filter((fl) => !fl.show || fl.show(d)).map((fl) => {
        const id = `field-${fl.key}`;
        const v = d[fl.key];
        const input = {
          text: () => <input className="admin-input" value={v ?? ""} onChange={(e) => set({ [fl.key]: e.target.value })} data-testid={id} />,
          link: () => <input className="admin-input" list="page-links" placeholder="/contatti oppure https://..." value={v ?? ""} onChange={(e) => set({ [fl.key]: e.target.value })} data-testid={id} />,
          textarea: () => <textarea className="admin-input min-h-[80px]" value={v ?? ""} onChange={(e) => set({ [fl.key]: e.target.value })} data-testid={id} />,
          number: () => <input type="number" min="0" className="admin-input !w-32" value={v ?? 0} onChange={(e) => set({ [fl.key]: Number(e.target.value) })} data-testid={id} />,
          select: () => <select className="admin-input" value={v ?? ""} onChange={(e) => set({ [fl.key]: e.target.value })} data-testid={id}>{fl.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>,
          switch: () => <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!v} onChange={(e) => set({ [fl.key]: e.target.checked })} data-testid={id} /> Attivo</label>,
          inline: () => <RichEditor key={section.id + fl.key} inline value={v} onChange={(h) => set({ [fl.key]: h })} testid={id} />,
          rich: () => <RichEditor key={section.id + fl.key} value={v} onChange={(h) => set({ [fl.key]: h })} testid={id} />,
          image: () => <ImageField fl={fl} d={d} set={set} id={id} />,
          images: () => <ImagesField value={v} onChange={(x) => set({ [fl.key]: x })} id={id} />,
          items: () => <ItemsField value={v} onChange={(x) => set({ [fl.key]: x })} id={id} />,
          category_covers: () => <CategoryCoversField id={id} />,
          category_multi: () => <CategoryPicker value={v} onChange={(x) => set({ [fl.key]: x })} id={id} />,
        }[fl.type];
        return <div key={fl.key}><Label>{fl.label}</Label>{input()}</div>;
      })}
      <datalist id="page-links">{links.map((l) => <option key={l} value={l} />)}</datalist>
    </div>
  );
};
